import type { Commodity, StrictOmit } from '@enmon/commons';
import {
  type SerializedCustomer,
  type SerializedEstablishment,
  type SerializedSupplyPoint,
  type SerializedMeter,
  type CountableApiResponse,
  type ViewSupplyPoints,
  ImportSupplyPointsFormField,
  type SupplyPointsStats,
  type SerializedSupplyPointByView,
  type UpdateSupplyPointDto,
  type CreateSupplyPointDto,
  type ImportSupplyPointCsvRow,
  type SupplyPointCategoryType,
  type SupplyPointStatus,
  type SerializedTenant,
  type BulkEditSupplyPointsDto,
  type BulkDeleteSupplyPointsResult,
  type BulkSupplyPointResult,
} from '@enmon/commons/lib/enmon';
import { getQuery } from '@/components/filter/helpers';
import { BaseApiClient } from '@/services/baseApiClient';
import { RequestError, type RequestOptions, uploadFile } from '@/utils/request';
import type { Pagination } from '@/hooks/usePagination';
import { captureException } from '@sentry/react';
import type { SerializedThirdParty } from '@enmon/commons/lib/tools';
import type { SerializedSupplyPointWithAnalysis } from '@enmon/commons/lib/enmon/supply-points/supplyPointWithAnalysis';
import type { PopulatedCustomer } from './customers';
import type { ImportArgs, ImportResult, WithAttachments } from './imports';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export type PopulatedSupplyPoint = StrictOmit<
  SerializedSupplyPoint,
  'distributor' | 'supplier' | '__v' | 'customer' | 'establishment' | 'owner'
> & {
  distributor?: SerializedThirdParty;
  supplier?: SerializedThirdParty;
  customer: PopulatedCustomer;
  establishment: Pick<SerializedEstablishment, '_id' | 'name' | 'type' | 'customer'>;
  owner?: Pick<SerializedTenant, '_id' | 'name'> | null;
};

export interface SupplyPointsFilter extends Pagination {
  commodities?: Commodity | Commodity[];
  supplyPoints?: SerializedSupplyPoint['_id'] | SerializedSupplyPoint['_id'][];
  meters?: SerializedMeter['_id'] | SerializedMeter['_id'][];
  establishments?: SerializedEstablishment['_id'] | SerializedEstablishment['_id'][];
  tags?: SerializedEstablishment['tags'] | SerializedEstablishment['tags'][];
  customer?: SerializedCustomer['_id'] | SerializedCustomer['_id'][];
  code?: SerializedSupplyPoint['code'];
  search?: string;
  view?: ViewSupplyPoints;
  supplyPointCategories?: SupplyPointCategoryType[];
  supplyPointStatuses?: SupplyPointStatus[];
}

// Argument types
export type GetSupplyPointArg = SerializedSupplyPoint['_id'];
export type GetSupplyPointsArg = SupplyPointsFilter;
export type GetSupplyPointsMetadataArg = SupplyPointsFilter;
export type GetSupplyPointsStats = SupplyPointsFilter;
export type GetSupplyPointsWithAnalysisArg = SupplyPointsFilter;
export type CreateSupplyPointArg = CreateSupplyPointDto;
export type ImportSupplyPointsArg = ImportArgs<WithAttachments>;
export type UpdateSupplyPointArg = {
  supplyPointId: SerializedSupplyPoint['_id'];
  body: UpdateSupplyPointDto;
};
export type MoveSupplyPointArg = {
  supplyPointId: SerializedSupplyPoint['_id'];
  establishmentId: SerializedEstablishment['_id'];
};
export type MoveSupplyPointsBulkArg = {
  supplyPointIds: ReadonlyArray<SerializedSupplyPoint['_id']>;
  establishmentId: SerializedEstablishment['_id'];
};
export type EditSupplyPointsBulkArg = BulkEditSupplyPointsDto;
export type DeleteSupplyPointArg = SerializedSupplyPoint['_id'];
export type DeleteSupplyPointsBulkArg = {
  supplyPointIds: ReadonlyArray<SerializedSupplyPoint['_id']>;
};
export type ToggleSupplyPointStatusArg = SerializedSupplyPoint['_id'];
export type ToggleSupplyPointStatusBody = {
  validFrom?: string;
};
export type GetSupplyPointHasHistoryArg = SerializedSupplyPoint['_id'];
export type SuspendSupplyPointsBulkArg = {
  supplyPointIds: ReadonlyArray<SerializedSupplyPoint['_id']>;
  validFrom?: string;
};

// Result types
export type GetSupplyPointResult = PopulatedSupplyPoint | null;
export type GetSupplyPointsResult = CountableApiResponse<PopulatedSupplyPoint[]> | null;
export type GetSupplyPointsMetadataResult<TView extends ViewSupplyPoints> = CountableApiResponse<
  SerializedSupplyPointByView[TView][]
> | null;
export type GetSupplyPointsStatsResult = SupplyPointsStats | null;
export type GetSupplyPointsWithAnalysisResult = CountableApiResponse<SerializedSupplyPointWithAnalysis[]> | null;
export type CreateSupplyPointResult = PopulatedSupplyPoint | null;
export type ImportSupplyPointsResult = ImportResult<ImportSupplyPointCsvRow> | null;
export type UpdateSupplyPointResult = PopulatedSupplyPoint | null;
export type MoveSupplyPointResult = undefined | null;
export type MoveSupplyPointsBulkResult = BulkSupplyPointResult | null;
export type EditSupplyPointsBulkResult = BulkSupplyPointResult | null;
export type DeleteSupplyPointResult = { archived: true } | null;
export type DeleteSupplyPointsBulkResult = BulkDeleteSupplyPointsResult | null;
export type ToggleSupplyPointStatusResult = PopulatedSupplyPoint | null;
export type GetSupplyPointHasHistoryResult = { hasHistory: boolean } | null;
export type SuspendSupplyPointsBulkResult = BulkSupplyPointResult | null;

export enum SupplyPointsBulkAction {
  Edit = 'edit',
  Move = 'move',
  Suspend = 'suspend',
  Delete = 'delete',
}

export type SupplyPointsBulkArgs = {
  [SupplyPointsBulkAction.Edit]: EditSupplyPointsBulkArg;
  [SupplyPointsBulkAction.Move]: MoveSupplyPointsBulkArg;
  [SupplyPointsBulkAction.Suspend]: SuspendSupplyPointsBulkArg;
  [SupplyPointsBulkAction.Delete]: DeleteSupplyPointsBulkArg;
};

export type SupplyPointsBulkResults = {
  [SupplyPointsBulkAction.Edit]: EditSupplyPointsBulkResult;
  [SupplyPointsBulkAction.Move]: MoveSupplyPointsBulkResult;
  [SupplyPointsBulkAction.Suspend]: SuspendSupplyPointsBulkResult;
  [SupplyPointsBulkAction.Delete]: DeleteSupplyPointsBulkResult;
};

const SUPPLY_POINTS_BULK_PATH = {
  [SupplyPointsBulkAction.Edit]: 'bulk-edit',
  [SupplyPointsBulkAction.Move]: 'bulk-move',
  [SupplyPointsBulkAction.Suspend]: 'bulk-suspend',
  [SupplyPointsBulkAction.Delete]: 'bulk-delete',
} as const satisfies Record<SupplyPointsBulkAction, string>;

export class SupplyPointsApiClient extends BaseApiClient {
  constructor() {
    super('api/supply-points');
  }

  public getOne(supplyPointId: GetSupplyPointArg, signal: RequestOptions['signal']): Promise<GetSupplyPointResult> {
    return this.http.get(`${supplyPointId}`, { signal });
  }

  public getHasHistory(
    supplyPointId: GetSupplyPointHasHistoryArg,
    signal: RequestOptions['signal'],
  ): Promise<GetSupplyPointHasHistoryResult> {
    return this.http.get(`${supplyPointId}/has-history`, { signal });
  }

  public get(filter: GetSupplyPointsArg, signal: RequestOptions['signal']): Promise<GetSupplyPointsResult> {
    return this.http.get('', {
      query: getQuery(filter),
      signal,
    });
  }

  public getWithAnalysis(
    filter: SupplyPointsFilter,
    signal: RequestOptions['signal'],
  ): Promise<GetSupplyPointsWithAnalysisResult> {
    return this.http.get('analysis', {
      query: getQuery({ ...filter, timezone }),
      signal,
    });
  }

  public getMetadata<TView extends ViewSupplyPoints = ViewSupplyPoints.Default>(
    filter: SupplyPointsFilter,
    view: TView,
    signal: RequestOptions['signal'],
  ): Promise<GetSupplyPointsMetadataResult<TView>> {
    return this.http.get('metadata', {
      query: getQuery({ ...filter, view }),
      signal,
    });
  }

  public getStats(filter: SupplyPointsFilter, signal: AbortSignal | undefined): Promise<GetSupplyPointsStatsResult> {
    return this.http.get('stats', { query: getQuery(filter), signal });
  }

  public create(body: CreateSupplyPointArg): Promise<CreateSupplyPointResult> {
    return this.http.post('', body);
  }

  public async importSupplyPoints({
    importFile,
    attachments,
  }: ImportSupplyPointsArg): Promise<ImportSupplyPointsResult> {
    const formData = new FormData();
    formData.append(ImportSupplyPointsFormField.ImportFile, importFile);

    attachments.forEach(attachment => {
      formData.append(ImportSupplyPointsFormField.Attachments, attachment);
    });
    try {
      return await uploadFile(`${this.http.urlPrefix}/import`, formData, {
        signal: undefined,
      });
    } catch (e) {
      if (e instanceof RequestError && e.response.status === 412) {
        return await e.response.json<ImportSupplyPointsResult>();
      }
      console.error(e);
      captureException(e);
      throw e;
    }
  }

  public update({ supplyPointId, body }: UpdateSupplyPointArg): Promise<UpdateSupplyPointResult> {
    return this.http.patch(`${supplyPointId}`, body);
  }

  public move({ supplyPointId, establishmentId }: MoveSupplyPointArg): Promise<MoveSupplyPointResult> {
    return this.http.put(`${supplyPointId}/move`, { establishmentId });
  }

  public moveBulk(body: MoveSupplyPointsBulkArg): Promise<MoveSupplyPointsBulkResult> {
    return this.http.put('bulk-move', body);
  }

  public editBulk(body: EditSupplyPointsBulkArg): Promise<EditSupplyPointsBulkResult> {
    return this.http.put('bulk-edit', body);
  }

  public delete(supplyPointId: DeleteSupplyPointArg): Promise<DeleteSupplyPointResult> {
    return this.http.delete(`${supplyPointId}`);
  }

  public deleteBulk(body: DeleteSupplyPointsBulkArg): Promise<DeleteSupplyPointsBulkResult> {
    return this.http.delete('', body);
  }

  public toggleStatus(
    supplyPointId: ToggleSupplyPointStatusArg,
    body: ToggleSupplyPointStatusBody = {},
  ): Promise<ToggleSupplyPointStatusResult> {
    return this.http.put(`${supplyPointId}/toggle-status`, body);
  }

  public suspendBulk(body: SuspendSupplyPointsBulkArg): Promise<SuspendSupplyPointsBulkResult> {
    return this.http.put('bulk-suspend', body);
  }

  public previewBulk<TAction extends SupplyPointsBulkAction>(
    action: TAction,
    body: SupplyPointsBulkArgs[TAction],
  ): Promise<SupplyPointsBulkResults[TAction]> {
    return this.http.post(`${SUPPLY_POINTS_BULK_PATH[action]}/preview`, body);
  }
}
